import React, { useEffect, useRef, useState } from 'react';
import {
  bool, func, oneOfType, shape, string
} from 'prop-types';
import axios from 'axios';
import { useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';

import Helper from './Helper';
import './media-gallery.style.scss';
import { componentReady } from '../publisher';
import { MediaGalleryDesktop as DesktopMediaGallery } from './subcomponents';
import { MediaHighlight } from './subcomponents/MediaHighlight/MediaHighlight';
import { MediaGalleryLoader } from './subcomponents/MediaGalleryLoader/MediaGalleryLoader';
import { dataModel } from './dataModel';

export const MediaGalleryDesktop = ({
  collectionId,
  hideProductImagesLabel,
  highlight,
  highRes,
  hover,
  itemId,
  marqueeRef,
  marqueeRowRef,
  media,
  showFavorites,
  showLoader,
}) => {
  const mediaMapRef = useRef(null);
  const [highResImages, setHighResImages] = useState([]);
  const storeId = useStoreId();
  const [hoverId, setHoverId] = useState('');
  useEffect(() => {
    if (hover) {
      window.LIFE_CYCLE_EVENT_BUS.on('supersku.hover', (hoverItemId) => {
        setHoverId(hoverItemId?.output);
      });
    }
  }, []);

  const [productRequest, productResponse] = useLazyDataModel('product', {
    variables: {
      itemId: hoverId,
      storeId,
    },
  });
  useEffect(() => {
    if (hoverId) {
      productRequest({ variables: { hoverId } });
    }
  }, [hoverId]);

  useEffect(() => {
    if (itemId) {
      componentReady({ itemId });
    }
  }, []);

  const { data, error, loading } = useDataModel('product', { variables: { itemId }, skip: !itemId });

  const collectionData = useDataModel('collection', { variables: { collectionId, storeId }, skip: !collectionId });
  const apiState = media ? null : Helper.getAPIState({
    data: collectionId ? collectionData.data : data,
    loading: collectionId ? collectionData.loading : loading,
    error: collectionId ? collectionData.error : error,
    fetchId: itemId || collectionId
  });

  const { product } = (data || {});
  const { collection } = (collectionData || {}).data || {};

  // for swatch Hover
  const mediaSourceHover = collectionId ? (collection || {}).media : ((productResponse.data || {}).product || {}).media;
  const mediaListHover = Helper.getInitialMediaList({ media: media || mediaSourceHover });
  const mainHover = Helper.getStructuredMediaList({ mediaList: mediaListHover, size: '600' });

  const mediaSource = collectionId ? (collection || {}).media : (product || {}).media;
  const mediaList = Helper.getInitialMediaList({ media: media || mediaSource });

  const thumbnailSize = '145';
  const thumbnails = Helper.getStructuredMediaList({ mediaList, size: thumbnailSize });
  const main = Helper.getStructuredMediaList({ mediaList, size: '600' });
  const zoom = Helper.getStructuredMediaList({ mediaList, size: '1000' });
  const displayedMedia = {
    thumbnails,
    main,
    zoom,
  };

  useEffect(() => {
    // TODO: Hook
    if (highRes) {
      const checkValidityPromise = [];
      zoom.forEach((zoomedImage) => {
        const imageCheckPromise = Helper.getHighResImages(zoomedImage, axios);
        checkValidityPromise.push(imageCheckPromise);
      });
      Promise.all(checkValidityPromise).then((responses) => {
        const images = responses.filter((resp) => !!resp);
        if (images.length) {
          setHighResImages(images);
        }

      }).catch(() => {});
    }
  }, [itemId]);

  if (apiState === 'loading') {
    return showLoader && <MediaGalleryLoader isMobile={false} />;
  }

  if (apiState === 'error' || !mediaList?.length) {
    return null;
  }

  if (highlight) {
    return <MediaHighlight itemId={itemId} />;
  }

  mediaMapRef.current = displayedMedia;

  if (highResImages) {
    mediaMapRef.current = {
      ...mediaMapRef.current,
      highResolution: highResImages
    };
  }

  return (
    <DesktopMediaGallery
      media={mediaMapRef.current}
      collection={collection}
      product={product}
      totalThumbnailsToShow={6}
      hideProductImagesLabel={hideProductImagesLabel}
      mainHover={mainHover}
      showFavorites={showFavorites}
      marqueeRef={marqueeRef}
      marqueeRowRef={marqueeRowRef}
    />
  );
};

MediaGalleryDesktop.displayName = 'MediaGalleryDesktop';

MediaGalleryDesktop.propTypes = {
  collectionId: string,
  hideProductImagesLabel: bool,
  highlight: bool,
  highRes: bool,
  hover: bool,
  itemId: string,
  marqueeRef: oneOfType([
    func,
    shape({})
  ]),
  marqueeRowRef: oneOfType([
    func,
    shape({})
  ]),
  media: shape({}),
  showLoader: bool,
  showFavorites: bool
};

MediaGalleryDesktop.defaultProps = {
  collectionId: null,
  hideProductImagesLabel: false,
  highlight: false,
  highRes: false,
  hover: false,
  itemId: null,
  marqueeRef: null,
  marqueeRowRef: null,
  media: null,
  showLoader: false,
  showFavorites: false
};

MediaGalleryDesktop.dataModel = dataModel;
